import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../../lib/analytics';
import { useConsultaModal } from '../../context/ConsultaModalContext';

export const LandingNav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { open } = useConsultaModal();

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 50;
            setScrolled((prev) => {
                if (prev === isScrolled) return prev;
                return isScrolled;
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Fecha menu ao redimensionar para desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) setMobileOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Bloqueia scroll e notifica o botão WhatsApp quando menu abre/fecha
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        window.dispatchEvent(new CustomEvent('nav:menu', { detail: { open: mobileOpen } }));
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const navLinks = [
        { label: 'Início', to: '/#' },
        { label: 'Sobre', to: '/#about' },
        { label: 'Serviços', to: '/#services' },
    ];

    return (
        <>
            <nav className={`fixed w-full z-50 transition-all duration-300 border-b font-sans ${scrolled
                ? 'bg-pacces-ebony/95 backdrop-blur-md border-pacces-gold/20 shadow-lg py-4'
                : 'bg-transparent backdrop-blur-sm border-white/10 py-6'
                }`}>
                <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                    {/* Logo */}
                    <div className="flex items-center gap-2">
                        <img
                            src="/logo/Logo-Pacces-Advogados-Branco.png"
                            alt="Pacces Advogados"
                            className={`transition-all duration-300 w-auto ${scrolled ? 'h-10' : 'h-12'}`}
                        />
                    </div>

                    {/* Desktop nav */}
                    <div className="hidden md:flex items-center gap-8 text-[12px] font-bold uppercase tracking-[0.2em] text-white/90">
                        {navLinks.map((link) => (
                            <Link key={link.label} className="hover:text-pacces-gold transition-colors" to={link.to}>
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* Desktop CTA */}
                    <button
                        onClick={() => { open(); trackEvent('nav_cta_click', { cta: 'agende_consulta' }); }}
                        className="hidden md:inline-flex bg-transparent border border-pacces-gold text-pacces-gold px-8 py-3 text-[12px] font-bold uppercase tracking-[0.2em] rounded-sm hover:bg-pacces-gold hover:text-pacces-ebony transition-all duration-300 shadow-sm active:scale-95"
                    >
                        Agende Consulta
                    </button>

                    {/* Hamburger button (mobile) */}
                    <button
                        aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
                        aria-expanded={mobileOpen}
                        className="md:hidden flex flex-col justify-center items-center gap-[5px] w-11 h-11 cursor-pointer"
                        onClick={() => setMobileOpen((v) => !v)}
                    >
                        <span className={`block h-[2px] bg-white transition-all duration-300 ${mobileOpen ? 'w-5 translate-y-[7px] rotate-45' : 'w-5'}`} />
                        <span className={`block h-[2px] bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0 w-5' : 'w-4'}`} />
                        <span className={`block h-[2px] bg-white transition-all duration-300 ${mobileOpen ? 'w-5 -translate-y-[7px] -rotate-45' : 'w-5'}`} />
                    </button>
                </div>
            </nav>

            {/* Mobile drawer overlay */}
            <div
                className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 md:hidden ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setMobileOpen(false)}
                aria-hidden="true"
            />

            {/* Mobile drawer */}
            <div
                className={`fixed top-0 right-0 z-50 h-full w-72 max-w-[85vw] bg-pacces-ebony border-l border-pacces-gold/20 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out md:hidden ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                {/* Drawer header */}
                <div className="flex items-center justify-between px-6 py-6 border-b border-pacces-gold/20">
                    <img src="/logo/Logo-Pacces-Advogados-Branco.png" alt="Pacces Advogados" className="h-10 w-auto" />
                    <button
                        aria-label="Fechar menu"
                        className="w-11 h-11 flex items-center justify-center text-white/70 hover:text-pacces-gold transition-colors cursor-pointer"
                        onClick={() => setMobileOpen(false)}
                    >
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Drawer links */}
                <nav className="flex-1 flex flex-col gap-1 px-4 py-8">
                    {navLinks.map((link) => (
                        <Link
                            key={link.label}
                            to={link.to}
                            onClick={() => setMobileOpen(false)}
                            className="px-4 py-4 text-[12px] font-bold uppercase tracking-[0.25em] text-white/80 hover:text-pacces-gold hover:bg-white/5 rounded-sm transition-all duration-200"
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                {/* Drawer CTA */}
                <div className="px-6 pb-10">
                    <button
                        onClick={() => {
                            setMobileOpen(false);
                            open();
                            trackEvent('nav_cta_click', { cta: 'agende_consulta_mobile' });
                        }}
                        className="block w-full text-center bg-pacces-gold text-pacces-ebony px-8 py-4 text-[12px] font-bold uppercase tracking-[0.25em] rounded-sm hover:bg-white transition-all duration-300 active:scale-95"
                    >
                        Agende Consulta
                    </button>
                </div>
            </div>
        </>
    );
};
